import React, { useState, useContext, useCallback, useRef } from 'react';
import MyContext from './MyContext';  // Import the context provider to share the selectedKey across components
import { Layout, Menu, message } from 'antd';
import {
  LineChartOutlined,
  PieChartOutlined,
  DatabaseOutlined,
  MinusSquareOutlined,
  TableOutlined,
  FundOutlined,
  BlockOutlined,
  CalculatorOutlined
} from '@ant-design/icons';

const { Sider } = Layout;


function Sidebar() {
  const { selectedKey, setSelectedKey, setIsModalVisible, selectedModule, setSelectedModule } = useContext(MyContext); // Use the context to track selected menu key
  const [collapsed, setCollapsed] = useState(false);  // State for sidebar collapse
  const [openKeys, setOpenKeys] = useState([]); // State for accordion menu
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const sidebarRef = useRef(null);
  const isResizing = useRef(false);
  // Accordion functionality for the sidebar
  const rootSubmenuKeys = [
    'acceptance-sampling', 'aggregate-planning', 'break-even', 'decision-making', 'financial-analysis',
    'forecasting', 'inventory', 'layout', 'learning-curve', 'line-balancing', 'linear-programming',
    'output-rates', 'preference-matrix', 'productivity', 'project-management', 'quality-performance',
    'scheduling', 'simulation', 'statistics', 'supply-chain', 'transportation-location',
    'transportation-production', 'waiting-lines'
  ];

  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (latestOpenKey && rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current) return;
    const newWidth = e.clientX;
    if (newWidth > 200 && newWidth < 600) {
      setSidebarWidth(newWidth);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const IMPLEMENTED_KEYS = [
    "1", "2", "3", "4", "5", "6", "8", "9", "10", "11", "12", "13", "14", "15", "17", "18", "19", "20", "21", "23", "24", "25", "26", "27", "28", "29", "30", "31", "33", "34", "39", "43", "47", "48", "49", "50", "51", "52", "53", "55", "56", "57", "58", "59", "60", "61", "62", "63", "64", "65", "66", "67", "68"
  ];


  const checkImplemented = (key) => {
    return IMPLEMENTED_KEYS.includes(key);
  };

  // This handles the click on menu items and updates the selected key in the context
  const handleMenuClick = (e) => {
    console.log("Sidebar selected key:", e.key);
    setSelectedKey(e.key); // Keep the actual key for menu highlighting
    if (!checkImplemented(e.key)) {
      message.info("This module is currently under development (coming soon).");
      return;
    }

    const modalKeys = [
      "1", "2", "3", "4", "5", "6", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30",
      "31", "32", "33", "34", "35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49", "50", "51", "52", "53", "54", "55", "56", "57", "58", "59", "60", "61",
      "62", "63", "64", "65", "66", "67", "68", "34",
    ];

    if (modalKeys.includes(e.key)) {
      setIsModalVisible(true);
    }
  };

  // Define main modules for easy reference
  const MAIN_MODULES = {
    "acceptance-sampling": "Acceptance Sampling Plans",
    "aggregate-planning": "Aggregate (Sales and Ops) Planning",
    "break-even": "Break-even Analysis",
    "decision-making": "Decision Making",
    "financial-analysis": "Financial Analysis",
    "forecasting": "Forecasting",
    "inventory": "Inventory Management",
    "layout": "Layout",
    "learning-curve": "Learning Curve Analysis",
    "line-balancing": "Line Balancing",
    "linear-programming": "Linear Programming",
    "output-rates": "Measuring Output Rates",
    "preference-matrix": "Preference Matrix",
    "productivity": "Productivity",
    "project-management": "Project Management",
    "quality-performance": "Quality and Performance",
    "scheduling": "Scheduling",
    "simulation": "Simulation",
    "statistics": "Statistics (mean, var, sd; normal dist)",
    "supply-chain": "Supply Chain Location Decisions",
    "transportation-location": "Transportation Method (Location)",
    "transportation-production": "Transportation Method (Production Planning)",
    "waiting-lines": "Waiting Lines",
    "Exponential-Distribution": "Exponential Distribution"
  };

  // NEW: Handle main module (SubMenu) click
  const handleSubMenuClick = (key) => {
    console.log("Main module selected:", key);
    setSelectedModule(key);  // Save the main module key (e.g., 'break-even')
    setSelectedKey(key);     // Also update selectedKey for consistency
  };


  // Define the menu items
  const menuItems = [
    {
      key: "title",
      icon: <MinusSquareOutlined />,
      label: !collapsed && (
        <div style={{ display: 'flex', alignItems: 'center', height: '50px' }}>
          <p style={{ fontWeight: 'bold', color: 'darkblue', fontSize: '14px', margin: 0 }}>
            Module Tree
          </p>
        </div>
      ),
    },
    {
      key: "acceptance-sampling",
      label: "Acceptance Sampling Plans",
      icon: <BlockOutlined />,
    },
    {
      key: "aggregate-planning",
      label: "Aggregate (Sales and Ops) Planning",
      icon: <BlockOutlined />,
      onTitleClick: () => handleSubMenuClick('aggregate-planning'),
      children: [
        { key: "25", label: "Aggregate Planning" },
        { key: "34", label: "Transportation Model" },
      ],
    },
    {
      key: "break-even",
      label: "Break-even Analysis",
      icon: <LineChartOutlined />,
      onTitleClick: () => handleSubMenuClick('break-even'),
      children: [
        { key: "2", label: "Breakeven Analysis (Cost vs. Revenues)" },
        { key: "1", label: "Crossover/ Cost-Volume Analysis" },
        { key: "3", label: "Multiproduct Breakeven Analysis" },
      ],
    },
    {
      key: "decision-making",
      label: "Decision Making",
      icon: <TableOutlined />,
      onTitleClick: () => handleSubMenuClick('decision-making'),
      children: [
        { key: "21", label: "Decision Tables" },
        { key: "22", label: "Decision Trees (Graphical)" },
        { key: "11", label: "Preference Matrix" },
      ],
    },
    {
      key: "35",
      label: "Financial Analysis",
      icon: <LineChartOutlined />,
    },
    {
      key: "forecasting",
      label: "Forecasting",
      icon: <PieChartOutlined />,
      onTitleClick: () => handleSubMenuClick('forecasting'),
      children: [
        { key: "12", label: "Error Analysis" },
        { key: "5", label: "Least Squares - Simple and Multiple Regression" },
        { key: "6", label: "Regression Projector" },
        { key: "4", label: "Time Series Analysis" },
      ],
    },
    {
      key: "inventory",
      label: "Inventory Management",
      icon: <DatabaseOutlined />,
      onTitleClick: () => handleSubMenuClick('inventory'),
      children: [
        { key: "9", label: "ABC Analysis" },
        { key: "8", label: "Economic Order Quantity (EOQ) Model" },
        { key: "13", label: "Economic Production Lot Size Model" },
        { key: "10", label: "Kanban computation" },
        { key: "23", label: "Quantity Discount (EOQ) Model" },
        { key: "19", label: "Reorder Point/Safety Stock (Discrete)" },
        { key: "20", label: "Reorder Point/Safety Stock (Normal)" },
        { key: "17", label: "Single Period Inventory (Discrete)" },
        { key: "18", label: "Single Period Inventory (Normal)" },
      ],
    },
    {
      key: "36",
      label: "Layout",
      icon: <BlockOutlined />,
    },
    {
      key: "learning-curve",
      label: "Learning Curve Analysis",
      icon: <LineChartOutlined />,
      onTitleClick: () => handleSubMenuClick('learning-curve'),
      children: [
        { key: "37", label: "Determine Learning Coefficient Given Times" },
        { key: "29", label: "Determine Times Given a Learning Coefficient" },
      ],
    },
    {
      key: "38",
      label: "Line Balancing",
      icon: <FundOutlined />,
    },
    {
      key: "39",
      label: "Linear Programming",
      icon: <BlockOutlined />,
      onClick: () => handleSubMenuClick("linear-programming"),
    },
    {
      key: "supply-chain",
      label: "Location",
      icon: <BlockOutlined />,
      onTitleClick: () => handleSubMenuClick('supply-chain'),
      children: [
        { key: "54", label: "Break-Even Analysis" },
        { key: "53", label: "Center of Gravity" },
        { key: "52", label: "Weighting Method" },
      ],
    },
    {
      key: "output-rates",
      label: "Measuring Output Rates",
      icon: <FundOutlined />,
      onTitleClick: () => handleSubMenuClick('output-rates'),
      children: [
        { key: "15", label: "Sample Size for Time Studies" },
        { key: "16", label: "Sample Size for Work Sampling" },
        { key: "14", label: "Time Study" },
      ],
    },
    {
      key: "40",
      label: "Productivity",
      icon: <CalculatorOutlined />,
    },
    {
      key: "project-management",
      label: "Project Management",
      icon: <FundOutlined />,
      onTitleClick: () => handleSubMenuClick('project-management'),
      children: [
        { key: "43", label: "Cost Budgeting" },
        { key: "42", label: "Crashing" },
        { key: "44", label: "Mean, Std dev given" },
        { key: "45", label: "Mean, Variance given" },
        { key: "41", label: "Single Time Estimate" },
        { key: "24", label: "Three Time Estimate" },
      ],
    },
    {
      key: "quality-performance",
      label: "Quality and Performance",
      icon: <BlockOutlined />,
      onTitleClick: () => handleSubMenuClick('quality-performance'),
      children: [
        { key: "65", label: "c-charts" },
        { key: "63", label: "p-charts" },
        { key: "66", label: "Process Capability, Cpk" },
        { key: "64", label: "x-bar Charts" },
      ],
    },
    {
      key: "46",
      label: "Scheduling",
      icon: <TableOutlined />,
    },
    {
      key: "47",
      label: "Simulation",
      icon: <PieChartOutlined />,
    },
    {
      key: "statistics",
      label: "Statistics (mean, var, sd; normal dist)",
      icon: <CalculatorOutlined />,
      onTitleClick: () => handleSubMenuClick('statistics'),
      children: [
        { key: "49", label: "Binomial Distribution" },
        { key: "48", label: "Data Analysis: Mean, Variance, etc." },
        { key: "51", label: "Exponential Distribution" },
        { key: "30", label: "Markov Analysis" },
        { key: "27", label: "Normal Distribution" },
        { key: "50", label: "Poisson Distribution" },
      ],
    },
    {
      key: "26",
      label: "Transportation Method (Location)",
      icon: <BlockOutlined />,
    },
    {
      key: "transportation-production",
      label: "Transportation Method (Production Planning)",
      icon: <BlockOutlined />,
      onTitleClick: () => handleSubMenuClick('transportation-production'),
      children: [
        { key: "67", label: "Aggregate Planning" },
        { key: "68", label: "Transportation Model" },
      ],
    },
    {
      key: "waiting-lines",
      label: "Waiting Lines",
      icon: <FundOutlined />,
      onTitleClick: () => handleSubMenuClick('waiting-lines'),
      children: [
        { key: "56", label: "Constant Service Times" },
        { key: "58", label: "Erlang-k Service Times" },
        { key: "61", label: "Finite System Size Model" },
        { key: "60", label: "Finite-Source Model" },
        { key: "57", label: "General Service Times" },
        { key: "59", label: "Multiple-Server Model" },
        { key: "55", label: "Single-Server Model" },
      ],
    },
    {
      key: "assignment",
      label: "Assignment",
      icon: <BlockOutlined />,
    },
    {
      key: "game-theory",
      label: "Game Theory",
      icon: <BlockOutlined />,
    },
    {
      key: "goal-programming",
      label: "Goal Programming",
      icon: <BlockOutlined />,
    },
    {
      key: "integer-programming",
      label: "Integer and Mixed Integer Programming",
      icon: <BlockOutlined />,
    },
    {
      key: "display-om",
      label: "Display OM Modules only",
      icon: <BlockOutlined />,
    },
    {
      key: "display-qm",
      label: "Display QM Modules only",
      icon: <BlockOutlined />,
    },
    {
      key: "display-all",
      label: "Display ALL Modules",
      icon: <BlockOutlined />,
    },
  ];


  const processMenuItems = (items) => {
    // 1. Separate special menu items that shouldn't be sorted alphabetically (like 'title' and 'display-*')
    const titleItem = items.find(item => item.key === 'title');
    const displayItems = items.filter(item =>
      item.key === 'display-om' || item.key === 'display-qm' || item.key === 'display-all'
    );
    const modules = items.filter(item =>
      item.key !== 'title' &&
      item.key !== 'display-om' &&
      item.key !== 'display-qm' &&
      item.key !== 'display-all'
    );

    // 2. Sort the modules list alphabetically by label
    const sortedModules = [...modules].sort((a, b) => {
      const labelA = typeof a.label === 'string' ? a.label : '';
      const labelB = typeof b.label === 'string' ? b.label : '';
      return labelA.localeCompare(labelB);
    });

    // 3. Process each module (and recursively its children)
    const processAndSort = (itemList) => {
      return itemList.map(item => {
        const isImplemented = item.key === 'title' || item.children || checkImplemented(item.key);
        const processedItem = {
          ...item,
          disabled: !isImplemented,
          style: !isImplemented ? { color: '#bfbfbf', cursor: 'not-allowed' } : {}, // Gray out and show not-allowed cursor if not implemented
        };

        if (item.children) {
          // Sort the children of this submenu alphabetically as well
          const sortedChildren = [...item.children].sort((a, b) => {
            const labelA = typeof a.label === 'string' ? a.label : '';
            const labelB = typeof b.label === 'string' ? b.label : '';
            return labelA.localeCompare(labelB);
          });
          processedItem.children = processAndSort(sortedChildren);
        }

        return processedItem;
      });
    };

    const processedSortedModules = processAndSort(sortedModules);

    // 4. Reconstruct items array in correct order (title first, sorted modules, then display settings)
    const result = [];
    if (titleItem) result.push(titleItem);
    result.push(...processedSortedModules);
    result.push(...displayItems);
    return result;
  };

  return (
    <div style={{ display: 'flex', position: 'relative' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="light"
        width={sidebarWidth}
        style={{
          height: 'calc(100vh - 64px)',
          boxShadow: "2px 0 8px rgba(0,0,0,0.05)",
          borderRight: "1px solid #e8e8e8"
        }}
        className="sidebar-scroll"
        ref={sidebarRef}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}  // Highlight the selected item based on selectedKey
          openKeys={openKeys}           // Control the opened submenus
          onOpenChange={onOpenChange}   // Handle opened/closed submenus
          onClick={handleMenuClick}  // Handle menu item clicks
          style={{ borderRight: 0, paddingBottom: "100px" }} // paddingBottom to ensure last items aren't obscured
          items={processMenuItems(menuItems)}
        />

        {!collapsed && selectedModule && (
          <div style={{ padding: '10px', borderTop: '1px solid #f0f0f0', fontSize: '12px', color: '#666' }}>
            Selected Module: {MAIN_MODULES[selectedModule]}
          </div>
        )}
      </Sider>

      {!collapsed && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            height: 'calc(100vh - 64px)',
            position: 'absolute',
            right: 0,
            zIndex: 101,
            transition: 'background-color 0.3s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#1890ff'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        />
      )}
    </div>
  );
}

export default Sidebar;
